import React from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import classNames from 'classnames';
import {graphql, useMutation, useLazyLoadQuery} from 'react-relay/hooks';
import {ConnectionHandler} from 'relay-runtime';
import {useForm, useWatch} from 'react-hook-form';
import {useAuthorizationState} from '../../../../../contexts/Authorization';
import {graphqlWhoamiNextConditionalQuery} from '../../../../../components/graphql';
import Button from '../../../../../components/button/button';
import {getRecaptchaToken} from '../../../../../utils/recaptcha';
import {invalidateProfilePosts, invalidateForMePosts} from '../../../../../utilities/invalidate-vibe-user-connections';
import MediaForm from '../../media-form';
import Image03Icon from '../../../../../assets/icons0/image-03.svg';
import XIcon from '../../../../../assets/icons/x.svg';

const createQuoteMutation = graphql`
    mutation createQuoteFormCreateQuoteMutation($request: CreateVibePostRequestInput!, $includeIsAuthorized: Boolean!) {
        createVibePost(request: $request) {
            id
            message
            created
            media
            removed
            spam
            likesCount
            likedByCurrentUser @include(if: $includeIsAuthorized)
            stemLikesCount
            stemLikedByCurrentUser @include(if: $includeIsAuthorized)
            viewsCount
            viewedByCurrentUser @include(if: $includeIsAuthorized)
            commentedByCurrentUser @include(if: $includeIsAuthorized)
            quotedByCurrentUser @include(if: $includeIsAuthorized)
            commentsCount
            quotesCount
            sharesCount
            sharedByCurrentUser @include(if: $includeIsAuthorized)
            poll {
                id
                options
                optionVotesCounts
                pollVotesCount
                votedByCurrentUser
                votedOptionIndex
            }
            quotedPost {
                id
                quotesCount
                quotedByCurrentUser
            }
            author {
                id
                vibe {
                    username
                }
            }
        }
    }
`;

const CreateQuoteForm = React.memo(({quotedPostId, onQuoteCreated, isAuthorized, onShowLoginPrompt}) => {
    const authorized = useAuthorizationState();
    const {whoamiNext} = useLazyLoadQuery(graphqlWhoamiNextConditionalQuery, {
        includeWhoamiNext: authorized
    });
    const currentUserId = whoamiNext?.id;
    const intl = useIntl();
    const {register, handleSubmit, formState: {errors}, reset, control, setFocus, setValue} = useForm({
        defaultValues: {
            message: '',
            media: []
        }
    });
    const message = useWatch({control, name: 'message'});
    const messageLength = message?.length || 0;
    const maxLength = 512;
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [showMedia, setShowMedia] = React.useState(false);
    
    const [createQuote, isCreatingQuote] = useMutation(createQuoteMutation);

    const onSubmit = handleSubmit(async (data) => {
        if (!isAuthorized) {
            onShowLoginPrompt?.();
            return;
        }

        const messageValue = data.message?.trim() || '';
        if (!messageValue || messageValue.length > maxLength) {
            return;
        }
        try {
            // Extract media URIs from form data
            const mediaUris = data.media
                ?.map(item => item?.uri)
                .filter(uri => uri && uri.trim())
                || [];
            
            const reCaptcha = await getRecaptchaToken();
            createQuote({
                variables: {
                    request: {message: messageValue, quotedPostId, media: mediaUris.length > 0 ? mediaUris : null, reCaptcha},
                    includeIsAuthorized: authorized
                },
                updater: (store) => {
                    const newQuote = store.getRootField('createVibePost');
                    if (!newQuote) return;
                    
                    // Append to quotes connection
                    if (quotedPostId) {
                        const root = store.getRoot();
                        const postRecord = root.getLinkedRecord('vibePostById', {id: quotedPostId});
                        if (postRecord) {
                            const connectionID = ConnectionHandler.getConnectionID(
                                postRecord.getDataID(),
                                'QuotesList_quotes'
                            );
                            if (connectionID) {
                                const connection = store.get(connectionID);
                                if (connection) {
                                    const edge = store.create(
                                        `client:newEdge:${newQuote.getValue('id')}`,
                                        'VibePostEdge'
                                    );
                                    edge.setLinkedRecord(newQuote, 'node');
                                    edge.setValue(`${Date.now()}`, 'cursor');
                                    ConnectionHandler.insertEdgeBefore(connection, edge);
                                }
                            }
                        }
                    }
                    
                    // Invalidate profile posts and for-me posts cache
                    invalidateProfilePosts(store);
                    invalidateForMePosts(store);
                },
                onCompleted: (res) => {
                    reset({message: '', media: []});
                    setShowMedia(false);
                    setIsExpanded(false);
                    onQuoteCreated?.(res?.createVibePost?.id);
                }
            });
        } catch (error) {
            console.error('reCAPTCHA error:', error);
        }
    });

    const handleMinimalClick = React.useCallback(() => {
        if (!isAuthorized) {
            onShowLoginPrompt?.();
            return;
        }
        setIsExpanded(true);
        setTimeout(() => {
            setFocus('message');
        }, 0);
    }, [setFocus, isAuthorized, onShowLoginPrompt]);

    const handleTextareaFocus = React.useCallback(() => {
        setIsExpanded(true);
    }, []);

    const handleCancel = React.useCallback(() => {
        reset({message: '', media: []});
        setShowMedia(false);
        setIsExpanded(false);
    }, [reset]);

    const handleAddMedia = React.useCallback(() => {
        if (!showMedia) {
            setShowMedia(true);
            setValue('media', [{uri: ''}], {shouldValidate: false, shouldDirty: true});
        }
    }, [showMedia, setValue]);

    const handleRemoveMedia = React.useCallback(() => {
        setShowMedia(false);
        setValue('media', [], {shouldValidate: false, shouldDirty: false});
    }, [setValue]);

    React.useEffect(() => {
        if (messageLength > 0 && isAuthorized) {
            setIsExpanded(true);
        }
    }, [messageLength, isAuthorized]);

    const handleKeyDown = React.useCallback((event) => {
        if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
            event.preventDefault();
            if (!isCreatingQuote) {
                onSubmit();
            }
        }
    }, [isCreatingQuote, onSubmit]);

    if (!isExpanded) {
        return (
            <div>
                <div 
                    className='background-color-white padding-1dot5rem border-radius-0dot75rem shadow-light-md cursor-pointer border-style-solid border-width-0dot0625rem border-color-gray-300'
                    onClick={handleMinimalClick}
                >
                    <div className='text-md color-gray-500'>
                        <FormattedMessage defaultMessage='Write a quote...' />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className='background-color-white padding-1dot5rem border-radius-0dot75rem shadow-light-md border-style-solid border-width-0dot0625rem border-color-gray-300'>
                <form onSubmit={onSubmit}>
                    <div className='display-flex justify-content-flex-end'>
                        <button
                            type='button'
                            aria-label='Close'
                            onClick={handleCancel}
                            disabled={isCreatingQuote}
                            className={classNames('background-color-transparent border-style-none padding-0 cursor-pointer', {
                                'opacity-0dot5 cursor-not-allowed': isCreatingQuote
                            })}
                        >
                            <XIcon className='display-block height-1dot5rem width-1dot5rem color-gray-500' />
                        </button>
                    </div>
                    <div className='padding-top-0dot75rem'>
                        <textarea
                            {...register('message', {
                                required: 'Quote message is required',
                                maxLength: {
                                    value: maxLength,
                                    message: `Quote cannot exceed ${maxLength} characters`
                                },
                                validate: (value) => {
                                    const trimmed = value?.trim() || '';
                                    if (!trimmed) {
                                        return 'Quote message cannot be empty';
                                    }
                                    return true;
                                }
                            })}
                            className='width-100percent padding-0dot75rem border-style-none text-md color-gray-900'
                            style={{resize: 'none', outline: 'none'}}
                            rows={3}
                            placeholder={intl.formatMessage({defaultMessage: 'Write a quote...'})}
                            maxLength={maxLength}
                            onKeyDown={handleKeyDown}
                            onFocus={handleTextareaFocus}
                        />
                    </div>
                    {!showMedia && (
                        <div className='padding-top-1rem'>
                            <Button
                                type='button'
                                size='sm'
                                color='primary'
                                plain={true}
                                clickHandler={handleAddMedia}
                                disabled={isCreatingQuote}
                            >
                                <div className='display-flex align-items-center column-gap-0dot5rem'>
                                    <Image03Icon className='display-block width-1rem height-1rem'/>
                                    <span>
                                        <FormattedMessage defaultMessage='Add media' />
                                    </span>
                                </div>
                            </Button>
                        </div>
                    )}
                    {showMedia && (
                        <MediaForm
                            control={control}
                            setValue={setValue}
                            formState={{errors}}
                            onRemove={handleRemoveMedia}
                        />
                    )}
                    <div className='padding-bottom-0dot75rem'>
                        {errors.message && (
                            <p className='text-sm color-error-600 padding-top-0dot5rem'>{errors.message.message}</p>
                        )}
                        <div className='display-flex align-items-center justify-content-space-between padding-top-1rem'>
                            <div className='text-sm color-gray-500'>
                                {messageLength}/{maxLength}
                            </div>
                            <div className='display-flex column-gap-0dot5rem'>
                                <Button
                                    type='submit'
                                    size='md'
                                    color='primary-gradient'
                                    isLoading={isCreatingQuote}
                                    disabled={!message?.trim() || messageLength > maxLength}
                                >
                                    <FormattedMessage defaultMessage='Quote' />
                                </Button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
});

export default CreateQuoteForm;


